import Image from "@tiptap/extension-image";
import { mergeAttributes } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import {
  ImageNodeView,
  IMAGE_RESIZE_STEP,
  MIN_IMAGE_WIDTH,
  IMAGE_ALIGNMENTS,
  IMAGE_ALIGN_CLASS,
  type ImageAlign,
} from "./image-node-view";

/** The alignment an element's classes name, or null when it names none. */
function alignFromElement(element: HTMLElement): ImageAlign | null {
  return (
    IMAGE_ALIGNMENTS.find((a) =>
      element.classList.contains(IMAGE_ALIGN_CLASS[a]),
    ) ?? null
  );
}

/**
 * A width the resize arithmetic can start from.
 *
 * The toolbar's presets store percentages, which `parseFloat` would happily
 * read as "50" and nudge to 82px — a jump from half the column to a thumbnail.
 * Only a pixel width is a number in the same space as the delta.
 */
function pixelWidth(value: unknown): number {
  const text = String(value ?? "");
  return text.endsWith("px") ? Number.parseFloat(text) || 0 : 0;
}

/**
 * `@tiptap/extension-image` plus a width and a caption.
 *
 * There is no official resize extension for tiptap v2, and the third-party ones
 * bring a dependency for what is one attribute and a node view. Extending the
 * installed Image keeps `allowBase64: false` and the existing `setImage`
 * command, so the upload path is untouched.
 */
export const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      /* Serialised as an inline `style`, not a Tailwind class: resting prose is
       * `dangerouslySetInnerHTML` of the stored string, and an arbitrary pixel
       * width would never have been compiled into the app's CSS. */
      width: {
        default: null,
        parseHTML: (el: HTMLElement) =>
          el.style.width || el.getAttribute("width") || null,
        renderHTML: (attrs: Record<string, unknown>) =>
          attrs.width ? { style: `width: ${attrs.width as string}` } : {},
      },
      /* The caption lives in a `<figcaption>`, so it must not also render as an
       * attribute on the `<img>`. */
      caption: {
        default: null,
        renderHTML: () => ({}),
      },
      /* Centred unless the author says otherwise: an image dropped into a
       * column of text reads as a figure, and a figure sits in the middle.
       * Rendered as a class by `renderHTML` below — on the `<figure>` when
       * there is a caption, so the caption travels with it, on the `<img>`
       * otherwise — so it must not also render as an attribute here. */
      align: {
        default: "center" as ImageAlign,
        parseHTML: (el: HTMLElement) => alignFromElement(el),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      /* Captioned images round-trip as a `<figure>`; a bare `<img>` is the
       * shape already stored in every existing description and must still
       * parse, or opening one would drop the image. */
      {
        tag: "figure.rte-figure",
        getAttrs: (element: HTMLElement | string) => {
          if (typeof element === "string") return false;
          const img = element.querySelector("img");
          if (!img) return false;
          return {
            src: img.getAttribute("src"),
            alt: img.getAttribute("alt"),
            title: img.getAttribute("title"),
            width: img.style.width || img.getAttribute("width") || null,
            caption:
              element.querySelector("figcaption")?.textContent?.trim() || null,
            align: alignFromElement(element) ?? alignFromElement(img),
          };
        },
      },
      { tag: "img[src]" },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    const { caption, align, ...imgAttrs } = HTMLAttributes as Record<
      string,
      unknown
    >;
    const text = node.attrs.caption as string | null;
    const alignClass =
      IMAGE_ALIGN_CLASS[(node.attrs.align as ImageAlign) ?? "center"] ??
      IMAGE_ALIGN_CLASS.center;
    const img = [
      "img",
      /* `mergeAttributes` concatenates `class`, so the alignment joins the
       * options' `max-w-full` rather than replacing it. On a
       * captioned image the class goes on the `<figure>` instead: the caption
       * has to sit under the image wherever the image sits. */
      mergeAttributes(
        this.options.HTMLAttributes,
        imgAttrs,
        text ? {} : { class: alignClass },
      ),
    ] as const;
    return text
      ? [
          "figure",
          { class: `rte-figure ${alignClass}` },
          img,
          ["figcaption", {}, text],
        ]
      : img;
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },

  /**
   * The keyboard route to resizing.
   *
   * The corner handles are pointer-only — a drag target cannot be operated
   * from a keyboard, and making them focusable would add four tab stops per
   * image to a surface whose whole point is that Tab does not leave the text.
   * Selecting the node is already keyboard-reachable (arrow into it), so the
   * shortcut hangs off the node selection instead.
   */
  addKeyboardShortcuts() {
    const nudge = (delta: number) => () => {
      const { state, view } = this.editor;
      const selection = state.selection;
      if (
        !(selection instanceof NodeSelection) ||
        selection.node.type.name !== this.name
      ) {
        return false;
      }
      const dom = view.nodeDOM(selection.from);
      const element = dom instanceof HTMLElement ? dom : null;
      const img = element?.querySelector("img");
      /* The stored width is authoritative when set; an image at its natural
       * size has no `width` attribute yet, so the first nudge has to measure. */
      const current =
        pixelWidth(selection.node.attrs.width) || img?.offsetWidth || 0;
      if (!current) return false;
      const max =
        element?.parentElement?.clientWidth ?? Number.MAX_SAFE_INTEGER;
      const next = Math.min(Math.max(current + delta, MIN_IMAGE_WIDTH), max);
      return this.editor.commands.updateAttributes(this.name, {
        width: `${Math.round(next)}px`,
      });
    };

    return {
      "Alt-ArrowRight": nudge(IMAGE_RESIZE_STEP),
      "Alt-ArrowLeft": nudge(-IMAGE_RESIZE_STEP),
    };
  },
}).configure({
  // Base64 would inline whole files into the stored HTML — every read of the
  // record would then carry the image bytes. Upload and store a URL.
  allowBase64: false,
  HTMLAttributes: {
    /* No corner radius. An image is content, not a control: rounding it made a
     * screenshot or a diagram — whose own edges are square — read as a card
     * pasted into the prose, and it showed at rest, where the picture is all
     * there is. Images stored before this still carry `rounded-md` in their
     * saved class list; `rich-text-editor.css` squares those off. */
    class: "max-w-full",
  },
});
